import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import WebApp from './web/App.tsx';
import BizApp from './biz/App.tsx';
import './index.css';

// Measure initial load time
const startTime = performance.now();

// Detect which app to render based on hostname
const determineAppType = () => {
  // Get the current hostname
  const hostname = window.location.hostname;
  
  // Check for business subdomain
  if (hostname.startsWith('biz.')) {
    console.log('Rendering Business Dashboard (biz subdomain detected)');
    return 'biz';
  }
  
  // Check for app subdomain
  if (hostname.startsWith('app.')) {
    console.log('Rendering Mobile App (app subdomain detected)');
    return 'mobile';
  }
  
  // Check if this is running in specific mode from env
  if (import.meta.env.VITE_APP_MODE === 'biz') {
    console.log('Rendering Business Dashboard (biz mode)');
    return 'biz';
  }
  
  if (import.meta.env.VITE_APP_MODE === 'web') {
    console.log('Rendering Web App (web mode)');
    return 'web';
  }
  
  // For main domain, render the web app
  if (hostname === 'nutfreenomad.com' || hostname === 'www.nutfreenomad.com') {
    console.log('Rendering Web App (main domain)');
    return 'web';
  }
  
  // For localhost or other domains, check URL params for testing
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('mode') === 'web') {
    console.log('Rendering Web App (mode=web param)');
    return 'web';
  }
  
  if (urlParams.get('mode') === 'biz') {
    console.log('Rendering Business Dashboard (mode=biz param)');
    return 'biz';
  }
  
  // Default to mobile app for other cases
  console.log('Rendering Mobile App (default)');
  return 'mobile';
};

// Determine which app to render
const appType = determineAppType();

// Log the current hostname and mode for debugging
console.log('Current hostname:', window.location.hostname);
console.log('App type:', appType);
console.log('Search params:', window.location.search);
console.log('Environment mode:', import.meta.env.VITE_APP_MODE);

// Create root and render appropriate app
const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    {appType === 'web' ? <WebApp /> : 
     appType === 'biz' ? <BizApp /> : 
     <App />}
  </StrictMode>
);

// Log performance metrics
window.addEventListener('load', () => {
  const loadTime = performance.now() - startTime;
  console.log(`App loaded in ${loadTime.toFixed(2)}ms`);
  
  // Report performance metrics
  if ('performance' in window && 'getEntriesByType' in performance) {
    const paintMetrics = performance.getEntriesByType('paint');
    const navigationTiming = performance.getEntriesByType('navigation')[0];
    
    if (paintMetrics.length > 0) {
      paintMetrics.forEach(metric => {
        console.log(`${metric.name}: ${metric.startTime.toFixed(2)}ms`);
      });
    }
    
    if (navigationTiming) {
      console.log(`DOM Content Loaded: ${(navigationTiming as any).domContentLoadedEventEnd.toFixed(2)}ms`);
      console.log(`Load Event: ${(navigationTiming as any).loadEventEnd.toFixed(2)}ms`);
    }
  }
});

// Register event listeners for touch events to improve mobile responsiveness
document.addEventListener('touchstart', () => {}, { passive: true });
document.addEventListener('touchmove', () => {}, { passive: true });
document.addEventListener('touchend', () => {}, { passive: true });